import React, { useState, useEffect, SyntheticEvent } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { MOUdata } from "../../components/grid/GridData.tsx";
import { externalpostApi, externalgetApi } from "../../services/externalApis.tsx";
import { SETTING_API_05, ITEM_API_01 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import {
  Messages,
  UITextMessages,
  MeasuringUnitMessages,
  MessageCategory,
  MessageCode,
  getMessageByCode,
  createDynamicMessage
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MeasuringUnit {
  measuringunitcode?: string | number;
  immeasuringunitcode?: string | number;
  measuringunit?: string;
  immeasuringunit?: string;
  descrip?: string;
  endate?: string | null;
  enuser?: string;
  sts?: string;
  id?: string | number;
  [key: string]: unknown;
}

interface Item {
  itemcode: number;
  itemname: string;
  measuringunitcode?: number;
  measuringunit?: string;
  [key: string]: any;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: string | { message?: string; error?: string };
  };
  request?: unknown;
  message?: string;
}

interface DeletePayload {
  COMPCODE: string;
  LOCCODE: string;
  measuringunitcode: string | number;
  ENUSER: string;
  STS: string;
  measuringunit?: string;
  descrip?: string;
  endate?: string | null;
  enuser?: string;
  sts?: string;
  MODE: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface RowWithId extends MeasuringUnit {
  id: string | number;
}

function ViewCreateUom(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<MeasuringUnit[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [selectedItem, setSelectedItem] = useState<MeasuringUnit | null>(null);
  const [data, setData] = useState<MeasuringUnit[]>([]);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    setPageData({
      title: MeasuringUnitMessages.pageTitle.view, // "Measuring Unit"
    } as PageData);
  }, [setPageData]);

  // Function to check if UOM is used in any items
  const checkUomUsage = async (measuringunitcode: string | number): Promise<boolean> => {
    try {
      setIsCheckingUsage(true);
      
      // Fetch items with the same UOM code
      const items = await externalgetApi(ITEM_API_01, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });

      console.log("Fetched items for usage check:", items);

      // Check if any items use this UOM (checking measuringunitcode field)
      const isUsed = items?.some((item: Item) => 
        Number(item.measuringunitcode) === Number(measuringunitcode)
      ) || false;

      console.log("Is UOM used:", isUsed);

      return isUsed;
    } catch (error) {
      console.error("Error checking UOM usage:", error);
      return false;
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const handleItemClick = async (id: string | number, actionText: string): Promise<void> => {
    console.log("handleItemClick called with:", { id, actionText });

    const selectedItem = data.find((item) => {
      // Try multiple possible ID field names
      return (
        item.measuringunitcode === id ||
        item.immeasuringunitcode === id ||
        item.id === id
      );
    });

    console.log("Found item:", selectedItem);

    if (actionText === UITextMessages.action.edit) {
      if (selectedItem) {
        navigate("/CreateUom", { state: { selectedItem, actionText } });
      }
    }

    if (actionText === UITextMessages.action.delete) {
      // Use the correct ID field from the data
      const actualId =
        selectedItem?.measuringunitcode ||
        selectedItem?.immeasuringunitcode ||
        selectedItem?.id;

      console.log("Actual ID to delete:", actualId);

      setSelectedId(actualId || null);
      setSelectedItem(selectedItem || null);
      setOpenDeleteDialog(true); // Open confirmation dialog immediately
    }
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      setIsLoading(true);

      // Log the request details for debugging
      console.log("=== DELETE REQUEST DETAILS ===");
      console.log("Selected ID:", selectedId);
      console.log("Selected Item:", selectedItem);
      console.log("Item fields:", Object.keys(selectedItem || {}));
      console.log("COMPCODE:", COMPCODE);
      console.log("LOCCODE:", STAFFMAPLOCODE);
      console.log("USERNAME:", USERNAME);

      // Validate required parameters
      if (!COMPCODE) {
        throw new Error(MeasuringUnitMessages.error.compCodeMissing);
      }
      if (!STAFFMAPLOCODE) {
        throw new Error(MeasuringUnitMessages.error.locCodeMissing);
      }
      if (!selectedId) {
        throw new Error(MeasuringUnitMessages.error.measuringUnitCodeMissing);
      }
      if (!USERNAME) {
        throw new Error(MeasuringUnitMessages.error.enuserMissing);
      }

      // Check if UOM is used in any items
      const isUsed = await checkUomUsage(selectedId);
      
      // If UOM is in use, show error message and don't delete
      if (isUsed) {
        setAlertMessage("Measuring unit cannot be deleted. It's already in use");
        setAlertType(MessageCategory.ERROR);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
        return;
      }

      // Build the delete payload - use the correct field names from the data
      const deletePayload: DeletePayload = {
        COMPCODE: COMPCODE || "",
        LOCCODE: STAFFMAPLOCODE || "",
        measuringunitcode: selectedId,
        ENUSER: USERNAME || "Admin",
        STS: CON_STATUS_DELETED || "D",
        // Include all fields that exist in the item
        measuringunit: selectedItem?.measuringunit || "",
        descrip: selectedItem?.descrip || "",
        endate: selectedItem?.endate || null,
        enuser: selectedItem?.enuser || USERNAME,
        sts: CON_STATUS_DELETED || "D",
        MODE: "DELETE",
      };

      console.log("DELETE Payload:", deletePayload);
      console.log("DELETE URL:", SETTING_API_05);

      // Send the request using POST
      const response = await externalpostApi(SETTING_API_05, deletePayload);

      console.log("DELETE Response:", response);

      if (response && (response as { success?: boolean }).success !== false) {
        // Remove from local state
        setData((prev) =>
          prev.filter((row) => {
            // Check all possible ID fields
            return (
              row.measuringunitcode !== selectedId &&
              row.immeasuringunitcode !== selectedId &&
              row.id !== selectedId
            );
          })
        );

        setFilteredRows((prev) =>
          prev.filter((row) => {
            return (
              row.measuringunitcode !== selectedId &&
              row.immeasuringunitcode !== selectedId &&
              row.id !== selectedId
            );
          })
        );

        // Use dynamic delete success message
        const successMessage = createDynamicMessage(
          MeasuringUnitMessages.success.delete,
          selectedItem?.measuringunit || "item"
        );
        
        setAlertMessage(successMessage);
        setAlertType(MessageCategory.SUCCESS);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
      } else {
        throw new Error((response as { message?: string })?.message || MeasuringUnitMessages.error.deleteOperationFailed);
      }
    } catch (error) {
      const err = error as ApiError;
      console.error("=== DELETE API ERROR ===", err);

      let errorMessage = MeasuringUnitMessages.error.deleteFailed;
      let errorDetails = "";

      if (err.response) {
        console.error("Response Status:", err.response.status);
        console.error("Response Data:", err.response.data);

        if (err.response.data) {
          if (typeof err.response.data === "string") {
            if (
              err.response.data.includes(
                "Object reference not set to an instance of an object"
              )
            ) {
              errorMessage = MeasuringUnitMessages.error.nullReference;
              errorDetails = MeasuringUnitMessages.error.nullReferenceDetail;
            } else if (err.response.data.trim() !== "") {
              errorMessage = err.response.data;
            }
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        switch (err.response.status) {
          case 400:
            errorMessage = errorMessage || MeasuringUnitMessages.error.badRequest;
            break;
          case 404:
            errorMessage = MeasuringUnitMessages.error.notFound;
            break;
          case 405:
            errorMessage = MeasuringUnitMessages.error.methodNotAllowed;
            break;
          case 500:
            errorMessage = errorMessage || MeasuringUnitMessages.error.serverError;
            break;
          default:
            errorMessage = errorMessage || `${MeasuringUnitMessages.error.serverErrorPrefix} ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage = MeasuringUnitMessages.error.noResponse;
      } else {
        errorMessage = err.message || MeasuringUnitMessages.error.unexpected;
      }

      setAlertMessage(
        `${errorMessage}${errorDetails ? ` (${errorDetails})` : ""}`
      );
      setAlertType(MessageCategory.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  const menuItems: MenuItem[] = [
    {
      text: UITextMessages.action.edit,
      icon: <BorderColorOutlinedIcon />,
      onClick: (id: string | number) => handleItemClick(id, UITextMessages.action.edit),
    },
    {
      text: UITextMessages.action.delete,
      icon: <DeleteIcon />,
      onClick: (id: string | number) => handleItemClick(id, UITextMessages.action.delete),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const params = {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        };

        console.log("Fetching measuring units with params:", params);

        const result = await externalgetApi(SETTING_API_05, "get", params);

        console.log("Fetched measuring units:", result);

        // Transform the data if needed
        const transformedData = ((result as MeasuringUnit[]) || []).map((item) => ({
          ...item,
          // Ensure consistent ID field
          id:
            item.measuringunitcode || item.immeasuringunitcode || Math.random(),
        }));

        setData(transformedData);
        setFilteredRows(transformedData);
        setIsLoading(false);
      } catch (err) {
        const error = err as ApiError;
        console.error("Error fetching measuring units:", error);
        setError(error);
        setData([]);
        setFilteredRows([]);
        setIsLoading(false);
        
        // Show error alert
        setAlertMessage(MeasuringUnitMessages.error.fetchFailed);
        setAlertType(MessageCategory.ERROR);
        setAlertOpen(true);
      }
    };

    if (COMPCODE && STAFFMAPLOCODE) {
      fetchData();
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Handle search input change - Search by both name and ID
  const handleSearchChange = (event: SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);
    
    // If search is empty, show all rows
    if (!searchValue) {
      setFilteredRows(data);
      return;
    }
    
    // Filter by both name and ID
    const filtered = data.filter((row) => {
      const nameMatch = (row.measuringunit || "")
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      
      const idMatch = (row.measuringunitcode?.toString() || "")
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      
      return nameMatch || idMatch;
    });
    
    setFilteredRows(filtered);
  };

  // Get search suggestions from both names and IDs
  const getSearchOptions = (): string[] => {
    const names = data
      .map((row) => row.measuringunit || "")
      .filter(Boolean);
    
    const ids = data
      .map((row) => row.measuringunitcode?.toString() || "")
      .filter(Boolean);
    
    return [...names, ...ids];
  };

  const addToView = (): void => {
    navigate("/CreateUom");
  };

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    setAlertMessage(MeasuringUnitMessages.warning.inlineEditNotSupported);
    setAlertType(MessageCategory.WARNING);
    setAlertOpen(true);
    return newRow;
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    // Ensure the grid has access to all fields
    id:
      row.measuringunitcode ||
      row.immeasuringunitcode ||
      row.id ||
      Math.random(),
    // Map fields if column definitions expect different names
    immeasuringunitcode: row.measuringunitcode,
    immeasuringunit: row.measuringunit,
    // Add any other field mappings needed
  }));

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link sx={styles.breadcrumb_link} underline="hover">
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            {MeasuringUnitMessages.breadcrumb.view}
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>{MOUdata.name}</Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={getSearchOptions()}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder={MeasuringUnitMessages.placeholder.search}
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton sx={styles.btn_appbar_search_icon}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />
              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
                disabled={isLoading}
              >
                {UITextMessages.button.add}
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={MOUdata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={MOUdata.name}
            gridid="measuringunitcode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        maxWidth="sm"
        fullWidth
        disableAutoFocus
        disableEnforceFocus
      >
        <DialogTitle id="delete-dialog-title">{MeasuringUnitMessages.dialog.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedItem ? (
              `Are you sure you want to delete the measuring unit "${selectedItem.measuringunit}"?`
            ) : (
              MeasuringUnitMessages.dialog.deleteConfirmation
            )}
          </DialogContentText>
          {/* {selectedItem?.descrip && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
              Description: {selectedItem.descrip}
            </DialogContentText>
          )} */}
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            disabled={isLoading || isCheckingUsage}
          >
            {UITextMessages.button.cancel}
          </Button>
          <Button
            id="btn_confirm"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
            disabled={isLoading || isCheckingUsage}
          >
            {isLoading ? MeasuringUnitMessages.button.deleting : UITextMessages.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewCreateUom;